import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { AvatarProps, AvatarImageProps, AvatarFallbackProps, AvatarContextValue, AvatarOverrides, ImageLoadingStatus } from './Avatar.types';
import { avatarStyles, StyledRoot, StyledImage, StyledFallback, FallbackGradientOverlay, resolveFontSize } from './Avatar.styles';
import { useOverrides } from '@shared/utils/overrides';
import { forwardRef } from '@shared/utils/forwardRef';
import { useStyleConfig } from '@shared/joykit/packages/core/common/utils/styleConfig';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';
import { useTimeout } from '@shared/hooks/useTimeout';
import { getInitials, getNameString } from './getInitials';
import { getAvatarColor } from './avatarColors';
import { useAvatarGroupContext } from './AvatarGroupContext';

const AvatarContext = createContext<AvatarContextValue | null>(null);

const useAvatarContext = () => {
  const ctx = useContext(AvatarContext);
  if (!ctx) {
    throw new Error('Avatar compound components must be rendered inside <Avatar>');
  }
  return ctx;
};

const EMPTY_OVERRIDES: AvatarOverrides = {};

const ROOT_DEFAULTS = { Root: StyledRoot };
const IMAGE_DEFAULTS = { Image: StyledImage };
const FALLBACK_DEFAULTS = { Fallback: StyledFallback };

const Avatar = forwardRef<'div', AvatarProps>((props, ref) => {
  const groupCtx = useAvatarGroupContext();
  const { size: sizeProp = 'md', name, src, alt, overrides = EMPTY_OVERRIDES, children, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, ...restProps } = props;
  const size = groupCtx?.size ?? sizeProp;

  const [imageStatus, setImageStatus] = useState<ImageLoadingStatus>('idle');

  const {
    Root: [Root, rootProps],
  } = useOverrides(ROOT_DEFAULTS, { Root: overrides.Root });

  const styles = useStyleConfig(avatarStyles, { size });

  const onImageStatusChange = useCallback((status: ImageLoadingStatus) => {
    setImageStatus(status);
  }, []);

  const contextValue = useMemo<AvatarContextValue>(
    () => ({
      size,
      name,
      src,
      alt,
      imageStatus,
      onImageStatusChange,
      overrides,
      styles,
    }),
    [size, name, src, alt, imageStatus, onImageStatusChange, overrides, styles],
  );

  const derivedLabel = ariaLabel ?? (name ? getNameString(name) : undefined);
  const hasAccessibleName = !!derivedLabel || !!ariaLabelledBy;

  return (
    <AvatarContext.Provider value={contextValue}>
      <Root
        ref={ref}
        data-slot="avatar"
        role={hasAccessibleName ? 'img' : undefined}
        aria-label={!ariaLabelledBy ? derivedLabel : undefined}
        __css={styles.root}
        {...rootProps}
        {...restProps}
      >
        {children ?? (
          <>
            <AvatarImage />
            <AvatarFallback />
          </>
        )}
      </Root>
    </AvatarContext.Provider>
  );
});

Avatar.displayName = 'Avatar';

const AvatarImage = forwardRef<'img', AvatarImageProps>((props, ref) => {
  const { src: srcProp, alt: altProp, onLoadingStatusChange, ...restProps } = props;
  const ctx = useAvatarContext();
  const src = srcProp ?? ctx.src;
  // alt intentionally defaults to '' — the accessible name lives on the parent
  // Avatar's role="img" + aria-label, so the <img> is decorative.
  const alt = altProp ?? ctx.alt ?? '';

  const {
    Image: [ImageComp, imageOverrideProps],
  } = useOverrides(IMAGE_DEFAULTS, { Image: ctx.overrides.Image });

  const [status, setStatus] = useState<ImageLoadingStatus>(src ? 'loading' : 'idle');

  // Effect 1: Detect cached images before paint so the fallback never flickers.
  // Effect 2 (below): Propagate status to context + callback. Kept separate so
  // detection logic and notification are independently testable/readable.
  useIsomorphicLayoutEffect(() => {
    if (!src) {
      setStatus('idle');
      return;
    }
    const probe = new Image();
    probe.src = src;
    if (probe.complete && probe.naturalWidth > 0) {
      setStatus('loaded');
    } else {
      setStatus('loading');
    }
  }, [src]);

  useIsomorphicLayoutEffect(() => {
    ctx.onImageStatusChange(status);
    onLoadingStatusChange?.(status);
  }, [status, ctx.onImageStatusChange, onLoadingStatusChange]);

  const handleLoad = useCallback(() => setStatus('loaded'), []);
  const handleError = useCallback(() => setStatus('error'), []);

  if (!src) return null;

  return (
    <ImageComp
      ref={ref}
      as="img"
      src={src}
      alt={alt}
      data-slot="avatar-image"
      onLoad={handleLoad}
      onError={handleError}
      __css={{
        ...ctx.styles.image,
        opacity: status === 'loaded' ? 1 : 0,
      }}
      {...imageOverrideProps}
      {...restProps}
    />
  );
});

AvatarImage.displayName = 'AvatarImage';

const AvatarFallback = forwardRef<'div', AvatarFallbackProps>((props, ref) => {
  const { name: nameProp, colorKey, delayMs, children, ...restProps } = props;
  const ctx = useAvatarContext();
  const name = nameProp ?? ctx.name;

  const {
    Fallback: [FallbackComp, fallbackOverrideProps],
  } = useOverrides(FALLBACK_DEFAULTS, { Fallback: ctx.overrides.Fallback });

  const timeout = useTimeout();
  const hasDelay = delayMs != null && delayMs > 0;
  const [isReady, setIsReady] = useState(!hasDelay);

  useIsomorphicLayoutEffect(() => {
    if (!hasDelay) {
      setIsReady(true);
      return;
    }

    if (ctx.imageStatus === 'loaded') return;

    if (ctx.imageStatus === 'error') {
      setIsReady(true);
      return;
    }

    setIsReady(false);
    timeout.set(() => setIsReady(true), delayMs!);
  }, [delayMs, ctx.imageStatus, timeout]);

  if (ctx.imageStatus === 'loaded') return null;
  if (!isReady) return null;

  const colorSeed = colorKey ?? (name ? getNameString(name) : undefined);
  const colorPair = colorSeed ? getAvatarColor(colorSeed) : undefined;

  const fontSize = resolveFontSize(ctx.size);
  const fallbackCss = {
    ...ctx.styles.fallback,
    ...(colorPair ? { backgroundColor: colorPair.bg, color: colorPair.text } : {}),
    fontSize: `${fontSize}px`,
    lineHeight: 1,
  };

  const content = children ?? (name ? getInitials(name) : null);

  return (
    <FallbackComp
      ref={ref}
      data-slot="avatar-fallback"
      __css={fallbackCss}
      {...fallbackOverrideProps}
      {...restProps}
    >
      {content}
      <FallbackGradientOverlay aria-hidden />
    </FallbackComp>
  );
});

AvatarFallback.displayName = 'AvatarFallback';

export { Avatar, AvatarImage, AvatarFallback };
